import { useState } from "react";
import { MapPin, Search } from "lucide-react";
import { weatherLook } from "./palette";

const dayLabel = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: "short" });

const dateLabel = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" });

export const WeatherStrip = ({ place, days, accent, onCity, loading, stale }) => {
  const [city, setCity] = useState("");

  if (!days?.length) {
    return (
      <div className="mt-5 rounded-2xl border border-white/10 bg-panel p-4" data-testid="weather-strip">
        <p className="text-[11px] uppercase tracking-widest" style={{ color: accent }}>
          Weather, a week ahead
        </p>
        <p className="mt-1.5 text-xs text-ink-soft">
          {loading ? "Looking up your forecast…" : "Tell me where you are and I'll put the forecast on your days."}
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <input
            data-testid="weather-city-input"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && city.trim() && onCity(city.trim())}
            placeholder="e.g. Ho Chi Minh City"
            className="min-h-[40px] flex-1 rounded-xl border border-white/10 bg-page px-4 text-sm outline-none placeholder:text-ink-soft"
          />
          <button
            data-testid="weather-city-save"
            onClick={() => city.trim() && onCity(city.trim())}
            className="inline-flex min-h-[40px] items-center gap-1.5 rounded-full px-4 text-xs font-semibold text-page"
            style={{ backgroundColor: accent }}
          >
            <Search className="h-3.5 w-3.5" /> Get forecast
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-5 rounded-2xl border border-white/10 bg-panel p-4" data-testid="weather-strip">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-[11px] uppercase tracking-widest" style={{ color: accent }}>
          Weather, a week ahead
        </p>
        {place?.name && (
          <span className="inline-flex items-center gap-1 text-xs text-ink-soft" data-testid="weather-place">
            <MapPin className="h-3 w-3" /> {place.name}{place.country ? `, ${place.country}` : ""}
          </span>
        )}
        {stale && (
          <span className="text-xs text-ink-soft" data-testid="weather-stale">
            · last known forecast, the weather service is busy
          </span>
        )}
        <button
          data-testid="weather-change-place"
          onClick={() => onCity(null)}
          className="ms-auto text-xs text-ink-soft underline decoration-dotted hover:text-ink"
        >
          Change place
        </button>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
        {days.map((d, i) => {
          const { Icon, label, blurb, tint } = weatherLook(d.code);
          const origin = i === 0 ? "origin-left" : i === days.length - 1 ? "origin-right" : "origin-center";
          return (
            <div
              key={d.day}
              title={blurb}
              data-testid={`weather-day-${d.day}`}
              tabIndex={0}
              className={`relative rounded-xl border bg-page px-2 py-2.5 text-center transition-transform duration-200 ease-out hover:z-20 hover:scale-[1.9] hover:shadow-2xl hover:shadow-black/60 focus-visible:z-20 focus-visible:scale-[1.9] focus:outline-none ${origin}`}
              style={{ borderColor: i === 0 ? `${accent}80` : "rgba(255,255,255,.10)" }}
            >
              <p className="text-[10px] uppercase tracking-wide text-ink-soft">
                {i === 0 ? "Today" : dayLabel(d.day)}
              </p>
              <p className="text-[10px] tabular-nums text-ink-soft">{dateLabel(d.day)}</p>
              <Icon className="mx-auto mt-1 h-5 w-5" style={{ color: tint }} />
              <p className="mt-0.5 text-[11px] font-semibold leading-tight">{label}</p>
              <p className="mt-0.5 text-[10px] leading-snug text-ink-soft">{blurb}</p>
              <p className="mt-1 text-xs font-semibold tabular-nums">{d.max}°</p>
              <p className="text-[10px] tabular-nums text-ink-soft">{d.min}°</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};
